import React, { useEffect, useRef, useState } from 'react';
import { DraggableProvided } from 'react-beautiful-dnd';
import { useSelector } from 'react-redux';
import classnames from 'classnames';
import moment, { unitOfTime } from 'moment';

import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { Alert, Link } from '@mm/components';
import WidgetModel, { DelayWidgetConfig } from '@mm/state/model/WidgetModel';

import {
  ErrorPortal,
  WidgetContainerRefProp,
} from '../../../editor/features/startBotPromoters/common/StartBotsDialogueEditor/WidgetByType/WidgetByType';
import { useCommonWidgetHandlers } from '../../helpers/widget-helpers/use-common-widget-handlers';
import { FacebookPageIcon } from '../common/FacebookPageIcon/FacebookPageIcon';
import { WidgetControls } from '../common/WidetControls/WidgetControls';
import './DelayWidget.pcss';
import { BotBuilderRoutes } from '../../../routes/bot-builder-routes';
import { DropdownOption } from '../../components/PrimarySelect/PrimarySelect';
import { TimePeriodSelector } from './TimePeriodSelector/TimePeriodSelector';
import { PlatformType } from '@mm/state/model/CampaignsModel';

const getDefaultValues = (s: any): [DelayWidgetConfig['measure'], number] => {
  switch (s) {
    case 'minute':
      return ['minute', 5];
    case 'hour':
      return ['hour', 1];
    default:
      return ['hour', 1];
  }
};

const convertToTime = (value: string, measure: unitOfTime.Base) => {
  return moment.duration(value, measure).as(measure);
};

const getMax = measure => moment.duration(23, 'hour').as(measure);

const getCounterValue = (value?: number) => {
  return value || 0;
};

export type DelayWidgetProps = {
  widget: WidgetModel;
  draggableProvided?: DraggableProvided;
  platforms?: PlatformType[];
} & WidgetContainerRefProp;

export const DelayWidget = ({
  widget,
  draggableProvided,
  platforms,
}: DelayWidgetProps) => {
  const chatbotId = useSelector(activeChatbotIdSelector);
  const commonWidgetHandlers = useCommonWidgetHandlers();

  const {
    config: { value, measure },
  } = widget;
  const [showAlert, setShowAlert] = useState(false);

  const deleteModalHeaderText = `Messages after this Delay Widget will not be sent to leads who were scheduled to receive them, 
  but new leads will get them normally. Are you sure you want to delete this widget?`;

  const mounted = useRef({});
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  });

  const onBlur = () => {
    setTimeout(() => {
      if (mounted.current) {
        setShowAlert(false);
      }
    }, 200);
  };

  const onError = () => setShowAlert(true);

  const onValueChange = value => {
    commonWidgetHandlers.update({
      ...widget,
      config: {
        ...widget.config,
        value,
      } as any,
    });
  };

  const onMeasureChange = (option: DropdownOption) => {
    commonWidgetHandlers.update({
      ...widget,
      config: {
        ...widget.config,
        value: !value
          ? getDefaultValues(option.id)
          : value > getMax(option.id)
          ? getMax(option.id)
          : value,
        measure: option.id,
      } as any,
    });
  };

  return (
    <div className={classnames('delay-widget')}>
      {widget.metadata?.alert === 'delay_blast_fb' && (
        <ErrorPortal widget={widget}>
          <Alert
            type="warning"
            className="delay-widget__warning-alert"
            showCloseIcon={false}
          >
            Widgets below this widget might not be sent if receiver interacted
            with the bot more than 24h ago
          </Alert>
        </ErrorPortal>
      )}

      <FacebookPageIcon />

      <div className="delay-widget__content">
        <span>Wait before sending the next widget:</span>

        <TimePeriodSelector
          className="delay-widget__controls"
          value={value}
          measure={measure}
          onBlur={onBlur}
          onError={onError}
          onValueChange={onValueChange}
          onMeasureChange={onMeasureChange}
        />

        {showAlert && (
          <Alert
            className="delay-widget__alert"
            showCloseIcon={false}
            type="warning"
          >
            <div>
              <span>
                A delay can't be set this far in the future. Try using
              </span>{' '}
              <Link
                to={BotBuilderRoutes.campaigns.getLink({
                  chatbotId,
                  kind: 'drip_campaigns',
                })}
                className="delay-widget__alert-action"
                onClick={e => e.preventDefault()}
              >
                a Drip Campaign instead.
              </Link>
            </div>
          </Alert>
        )}
        <WidgetControls
          widget={widget}
          draggableProvided={draggableProvided}
          deleteModalHeaderText={deleteModalHeaderText}
          platforms={platforms}
        />
      </div>
    </div>
  );
};
